import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ChopKatalog } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormButtonBarComponent } from '@shared/form-button-bar/form-button-bar.component';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { applyChopServerValidationError } from '../../chop-server-errors';

const FIELD_MAP = { Jahr: 'jahr', 'Bezeichnung.De': 'bezeichnung.De' } as const;

@Component({
  selector: 'app-chop-katalog-dialog',
  templateUrl: './chop-katalog-dialog.component.html',
  styleUrls: ['./chop-katalog-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FormTextInputComponent, FormNumberInputComponent, FormButtonBarComponent],
})
export class ChopKatalogDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  private katalog: ChopKatalog | undefined = this.dialogConfig.data?.katalog;
  form = this.formBuilder.nonNullable.group({
    jahr: [this.katalog?.jahr ?? new Date().getFullYear(), Validators.required],
    bezeichnung: this.formBuilder.nonNullable.group({
      De: [this.katalog?.bezeichnung?.De ?? '', Validators.required],
      Fr: [this.katalog?.bezeichnung?.Fr ?? ''],
      It: [this.katalog?.bezeichnung?.It ?? ''],
    }),
  });
  submitting = false;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const value = this.form.getRawValue() as ChopKatalog;
    const action = this.katalog
      ? this.chopService.updateKatalog(this.katalog.id, { ...this.katalog, ...value })
      : this.chopService.createKatalog(value);
    action.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err) => {
        if (!applyChopServerValidationError(this.form, err?.error, FIELD_MAP)) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
            detail: err?.error?.message,
          });
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
